import { AgCheckBoxCellRendererComponent } from 'components/AgGrid/AgCheckBoxCellRendererComponent';
import { AgGridCellTooltipComponent, AgGridCellTooltipValueGetter } from 'components/AgGrid/AgGridCellTooltipComponent';
import { AgTooltipHeaderComponent } from 'components/AgGrid/AgTooltipHeaderComponent';
import { WorkOrderStatusEnum } from 'gql/graphql';
import type { PriceListUerRowData } from 'gql/graphql';

import type { ColDef } from 'ag-grid-community';
import { useCallback, useMemo, useState } from 'react';

import { UnitCostWithOtherCellRendererComponent } from '../components/UnitCostWithOtherCellRendererComponent';
import { ValueNoteCellRendererComponent } from '../components/ValueNoteCellRendererComponent';

interface IUseUerWorkOrderChangePriceListColumns {
    allIds?: number[];
    constructionTypeId: number;
    columnsFilters: ColDef[];
    workOrderStatus?: WorkOrderStatusEnum;
    showNoteInProgress?: boolean;
}

const customHeaderName = () => (
    <span>
        Стоимость
        <div style={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}>
            единицы УЕР в текущем уровне цен (с учетом прочих и лимитированных затрат), руб.без НДС
        </div>
    </span>
);

export const useUerWorkOrderPriceListColumns = (props: IUseUerWorkOrderChangePriceListColumns) => {
    const { constructionTypeId, columnsFilters, workOrderStatus, showNoteInProgress } = props;

    const [checkedIds, setCheckedIds] = useState<Record<string, boolean>>({});
    const [valuesNotes, setValuesNotes] = useState<Record<string, string>>({});

    const onCheckChange = useCallback((checked: number[]) => {
        setCheckedIds(checked.reduce((acc, id) => ({ ...acc, [id]: true }), {}));
    }, []);

    const columns = useMemo<ColDef<PriceListUerRowData>[]>(() => {
        const checked = Object.keys(checkedIds).map((id) => Number(id));

        return [
            {
                field: 'checkbox' as any,
                lockPosition: 'left',
                headerName: '',
                minWidth: 60,
                maxWidth: 60,
                cellRenderer: AgCheckBoxCellRendererComponent,
                cellRendererParams: {
                    checkedIds: checked,
                    onCheckChange,
                },
                resizable: true,
            },
            {
                field: 'sectionName',
                headerName: 'Раздел',
                width: 150,
                resizable: true,
                tooltipComponent: AgGridCellTooltipComponent,
                tooltipValueGetter: AgGridCellTooltipValueGetter({}),
                ...((columnsFilters.find((f) => f.field === 'sectionName') || {}) as ColDef<PriceListUerRowData>),
            },
            {
                field: 'groupName',
                headerName: 'Подраздел',
                width: showNoteInProgress && workOrderStatus !== WorkOrderStatusEnum.InProgress ? 290 : 220,
                resizable: true,
                tooltipComponent: AgGridCellTooltipComponent,
                tooltipValueGetter: AgGridCellTooltipValueGetter({}),
                ...((columnsFilters.find((f) => f.field === 'groupName') || {}) as ColDef<PriceListUerRowData>),
            },
            {
                field: 'code',
                headerName: 'Шифр',
                width: 150,
                resizable: true,
                ...((columnsFilters.find((f) => f.field === 'code') || {}) as ColDef<PriceListUerRowData>),
            },
            {
                field: 'name',
                headerName: 'Наименование',
                width: showNoteInProgress && workOrderStatus !== WorkOrderStatusEnum.InProgress ? 310 : 220,
                resizable: true,
                ...((columnsFilters.find((f) => f.field === 'name') || {}) as ColDef<PriceListUerRowData>),
                tooltipComponent: AgGridCellTooltipComponent,
                tooltipValueGetter: AgGridCellTooltipValueGetter({}),
            },
            {
                field: 'unit',
                headerName: 'Ед.изм',
                width: 100,
                resizable: true,
            },
            {
                field: 'unitCostWithOther' as any,
                headerComponent: AgTooltipHeaderComponent,
                headerComponentParams: {
                    customHeaderName,
                    displayName:
                        'Стоимость единицы УЕР в текущем уровне цен (с учетом прочих и лимитированных затрат), руб.без НДС',
                },
                cellRenderer: UnitCostWithOtherCellRendererComponent,
                cellRendererParams: {
                    constructionTypeId,
                },
                width: 150,
                resizable: true,
                type: 'rightAligned',
                headerClass: 'center-aligned-header',
            },
            ...(!showNoteInProgress || workOrderStatus === WorkOrderStatusEnum.InProgress
                ? [
                      {
                          field: 'valueNote' as any,
                          headerName: 'Примечание',
                          cellRenderer: ValueNoteCellRendererComponent,
                          cellRendererParams: {
                              valuesNotes,
                              setValuesNotes,
                              checkedIds: checked,
                          },
                          width: 170,
                          resizable: true,
                      },
                  ]
                : []),
        ];
    }, [
        checkedIds,
        columnsFilters,
        constructionTypeId,
        onCheckChange,
        showNoteInProgress,
        valuesNotes,
        workOrderStatus,
    ]);

    const uerInputs = useMemo(
        () =>
            Object.keys(checkedIds).map((mtrRowId) => ({
                rationale: valuesNotes[mtrRowId] || '',
                uerBaseRowId: parseInt(mtrRowId, 10),
            })),
        [checkedIds, valuesNotes],
    );

    return { columns, uerInputs, checkedIds, setCheckedIds };
};
